import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { apiRequests } from "../../shared/api/api";
import { toast } from "sonner";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Button } from "src/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Label } from "src/components/ui/label";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Badge } from "src/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/components/ui/alert-dialog";
import { Skeleton } from "src/components/ui/skeleton";
import { ScrollArea } from "src/components/ui/scroll-area";
import { Checkbox } from "src/components/ui/checkbox";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useDropzone } from "react-dropzone";
import {
  Plus,
  Pencil,
  Trash2,
  FolderPlus,
  FileText,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Copy,
  X,
  CheckCircle,
} from "lucide-react";
import {
  BoldOutlined,
  FontColorsOutlined,
  CodeOutlined,
} from "@ant-design/icons";
import { Tooltip as AntTooltip } from "antd";

const normalizeParentId = (val) =>
  val === 0 || val === "0" || val === "" ? null : val;

const ImageUploader = ({ onFilesSelected }) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );
      setFiles((prev) => [...prev, ...newFiles]);
      setPreviews((prev) => [...prev, ...newFiles.map((f) => f.preview)]);
      onFilesSelected && onFilesSelected([...files, ...newFiles]);
    },
  });

  const removeFile = (index) => {
    const newFiles = [...files];
    const removed = newFiles.splice(index, 1)[0];
    if (removed.preview) URL.revokeObjectURL(removed.preview);
    setFiles(newFiles);
    setPreviews(newFiles.map((f) => f.preview));
    onFilesSelected && onFilesSelected(newFiles);
  };

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [files]);

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${isDragActive ? "border-blue-500 bg-blue-50" : "border-neutral-300 hover:border-neutral-400"}`}
      >
        <input {...getInputProps()} />
        <p className="text-sm text-neutral-600">
          {isDragActive
            ? "Отпустите файлы для загрузки"
            : "Перетащите изображения сюда или кликните для выбора"}
        </p>
      </div>
      {previews.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          {previews.map((preview, idx) => (
            <div key={idx} className="relative group border rounded">
              <img
                src={preview}
                alt=""
                className="w-full h-24 object-cover rounded"
              />
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition"
                onClick={() => removeFile(idx)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TagInput = ({ value = [], onChange }) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  const tags = Array.isArray(value) ? value : [];

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInputValue("");
  };

  const removeTag = (removedTag) => {
    onChange(tags.filter((tag) => tag !== removedTag));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border rounded-md px-2 py-1 min-h-[36px] bg-white">
      {tags.map((tag, index) => (
        <Badge
          key={index}
          variant="secondary"
          className="gap-1 px-2 py-0.5 font-sans font-normal"
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(tag)}
            className="ml-1 hover:text-black/70"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={
          tags.length === 0 ? "Введите теги через запятую или Enter" : ""
        }
        className="flex-1 min-w-[120px] outline-none text-sm font-sans bg-transparent"
      />
    </div>
  );
};

const HtmlEditor = ({ value = "", onChange }) => {
  const editorRef = useRef(null);
  const [showSource, setShowSource] = useState(false);
  const [sourceValue, setSourceValue] = useState(value);

  useEffect(() => {
    if (editorRef.current && !showSource) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [value, showSource]);

  const exec = (command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    notifyChange();
  };

  const applyColor = (color) => {
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("foreColor", false, color);
    notifyChange();
  };

  const applyBgColor = (color) => {
    editorRef.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("hiliteColor", false, color);
    notifyChange();
  };

  const notifyChange = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const toggleSource = () => {
    if (!showSource) {
      setSourceValue(editorRef.current?.innerHTML || "");
      setShowSource(true);
    } else {
      if (editorRef.current) {
        editorRef.current.innerHTML = sourceValue;
      }
      onChange && onChange(sourceValue);
      setShowSource(false);
    }
  };

  const toolbarBtnStyle = {
    padding: "4px 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "#fafafa",
    cursor: "pointer",
    fontSize: 13,
    lineHeight: "20px",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  };

  return (
    <div
      style={{
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          flexWrap: "wrap",
          padding: "6px 8px",
          background: "#f5f5f5",
          borderBottom: "1px solid #d9d9d9",
        }}
      >
        <AntTooltip title="Жирный">
          <button
            type="button"
            style={toolbarBtnStyle}
            onMouseDown={(e) => {
              e.preventDefault();
              exec("bold");
            }}
          >
            <BoldOutlined />
          </button>
        </AntTooltip>
        <AntTooltip title="Курсив">
          <button
            type="button"
            style={toolbarBtnStyle}
            onMouseDown={(e) => {
              e.preventDefault();
              exec("italic");
            }}
          >
            <i style={{ fontStyle: "italic", fontFamily: "serif" }}>I</i>
          </button>
        </AntTooltip>
        <AntTooltip title="Подчёркнутый">
          <button
            type="button"
            style={toolbarBtnStyle}
            onMouseDown={(e) => {
              e.preventDefault();
              exec("underline");
            }}
          >
            <u>U</u>
          </button>
        </AntTooltip>
        <div style={{ width: 1, background: "#d9d9d9", margin: "0 2px" }} />
        <AntTooltip title="Красный текст">
          <button
            type="button"
            style={{ ...toolbarBtnStyle, color: "#f5222d" }}
            onMouseDown={(e) => {
              e.preventDefault();
              applyColor("#f5222d");
            }}
          >
            <FontColorsOutlined /> <span style={{ fontSize: 11 }}>Красный</span>
          </button>
        </AntTooltip>
        <AntTooltip title="Белый текст (на тёмном фоне)">
          <button
            type="button"
            style={{
              ...toolbarBtnStyle,
              background: "#333",
              color: "#fff",
              borderColor: "#555",
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              applyColor("#ffffff");
            }}
          >
            <FontColorsOutlined /> <span style={{ fontSize: 11 }}>Белый</span>
          </button>
        </AntTooltip>
        <AntTooltip title="Чёрный текст">
          <button
            type="button"
            style={{ ...toolbarBtnStyle }}
            onMouseDown={(e) => {
              e.preventDefault();
              applyColor("#000000");
            }}
          >
            <FontColorsOutlined /> <span style={{ fontSize: 11 }}>Чёрный</span>
          </button>
        </AntTooltip>
        <div style={{ width: 1, background: "#d9d9d9", margin: "0 2px" }} />
        <AntTooltip title="Выделение жёлтым">
          <button
            type="button"
            style={{ ...toolbarBtnStyle, background: "#ffe58f" }}
            onMouseDown={(e) => {
              e.preventDefault();
              applyBgColor("#ffe58f");
            }}
          >
            ✏️ Фон
          </button>
        </AntTooltip>
        <AntTooltip title="Убрать форматирование">
          <button
            type="button"
            style={toolbarBtnStyle}
            onMouseDown={(e) => {
              e.preventDefault();
              exec("removeFormat");
            }}
          >
            ✕ Сброс
          </button>
        </AntTooltip>
        <div style={{ width: 1, background: "#d9d9d9", margin: "0 2px" }} />
        <AntTooltip
          title={showSource ? "Вернуться к редактору" : "Редактировать HTML"}
        >
          <button
            type="button"
            style={{
              ...toolbarBtnStyle,
              background: showSource ? "#1677ff" : "#fff",
              color: showSource ? "#fff" : undefined,
              borderColor: showSource ? "#1677ff" : undefined,
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              toggleSource();
            }}
          >
            <CodeOutlined /> HTML
          </button>
        </AntTooltip>
      </div>

      {showSource ? (
        <Textarea
          value={sourceValue}
          rows={6}
          onChange={(e) => setSourceValue(e.target.value)}
          style={{
            borderRadius: 0,
            fontFamily: "monospace",
            fontSize: 12,
            border: "none",
            resize: "vertical",
          }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={notifyChange}
          onBlur={notifyChange}
          style={{
            minHeight: 120,
            padding: "8px 12px",
            outline: "none",
            lineHeight: 1.6,
            fontSize: 14,
            background: "#fff",
          }}
        />
      )}
    </div>
  );
};

const BlogPage = ({ token }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("sites");
  
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [siteFoldersModalOpen, setSiteFoldersModalOpen] = useState(false);
  const [selectedSiteForFolders, setSelectedSiteForFolders] = useState(null);

  const [postFolderFilter, setPostFolderFilter] = useState("all");
  const [postStatusFilter, setPostStatusFilter] = useState("all");

  useEffect(() => {
    if (token) apiRequests.setToken(token);
  }, [token]);

  // --- Запросы данных ---
  const { data: sites = [], isLoading: sitesLoading } = useQuery({
    queryKey: ["blog-sites", token],
    queryFn: () => apiRequests.blog.getSites({ token }).then((res) => res.data),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 минут
    cacheTime: 10 * 60 * 1000,
  });

  const { data: folders = [], isLoading: foldersLoading } = useQuery({
    queryKey: ["blog-folders", token],
    queryFn: () =>
      apiRequests.blog.getFolders({ token }).then((res) => res.data),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const { data: posts = [], isLoading: postsLoading } = useQuery({
    queryKey: ["blog-posts", token, postFolderFilter, postStatusFilter],
    queryFn: () => {
      const params = { token };
      if (postFolderFilter && postFolderFilter !== "all")
        params.folder_id = postFolderFilter;
      if (postStatusFilter && postStatusFilter !== "all")
        params.status = postStatusFilter;
      return apiRequests.blog.getPosts(params).then((res) => res.data);
    },
    enabled: !!token,
    staleTime: 2 * 60 * 1000, // 2 минуты для постов (чаще обновляются)
    cacheTime: 5 * 60 * 1000,
  });

  const siteDeleteMutation = useMutation({
    mutationFn: (id) => apiRequests.blog.deleteSite(id, { token }),
    onSuccess: () => {
      toast.success("Сайт деактивирован");
      invalidateAll();
    },
  });
  const invalidateAll = () => {
    queryClient.invalidateQueries(["blog-sites"]);
    queryClient.invalidateQueries(["blog-folders"]);
    queryClient.invalidateQueries(["blog-posts"]);
  };

  const siteCreateMutation = useMutation({
    mutationFn: (data) => apiRequests.blog.createSite(data, { token }),
    onSuccess: () => {
      toast.success("Сайт создан");
      invalidateAll();
      setSiteModalOpen(false);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Ошибка"),
  });

  const siteUpdateMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiRequests.blog.updateSite(id, data, { token }),
    onSuccess: () => {
      toast.success("Сайт обновлён");
      invalidateAll();
      setSiteModalOpen(false);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Ошибка"),
  });

  const siteFoldersMutation = useMutation({
    mutationFn: ({ siteId, folderIds }) =>
      apiRequests.blog.setSiteFolders(
        siteId,
        { folder_ids: folderIds },
        { token },
      ),
    onSuccess: () => {
      toast.success("Папки сайта обновлены");
      invalidateAll();
      setSiteFoldersModalOpen(false);
    },
  });

  const folderCreateMutation = useMutation({
    mutationFn: (data) => apiRequests.blog.createFolder(data, { token }),
    onSuccess: () => {
      toast.success("Папка создана");
      invalidateAll();
      setFolderModalOpen(false);
    },
  });

  const folderUpdateMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiRequests.blog.updateFolder(id, data, { token }),
    onSuccess: () => {
      toast.success("Папка обновлена");
      invalidateAll();
      setFolderModalOpen(false);
    },
  });

  const folderDeleteMutation = useMutation({
    mutationFn: (id) => apiRequests.blog.deleteFolder(id, { token }),
    onSuccess: () => {
      toast.success("Папка удалена");
      invalidateAll();
    },
  });

  const postCreateMutation = useMutation({
    mutationFn: (data) => apiRequests.blog.createPost(data, { token }),
    onSuccess: () => {
      toast.success("Пост создан");
      invalidateAll();
      setPostModalOpen(false);
    },
  });

  const postUpdateMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiRequests.blog.updatePost(id, data, { token }),
    onSuccess: () => {
      toast.success("Пост обновлён");
      invalidateAll();
      setPostModalOpen(false);
    },
  });

  const postDeleteMutation = useMutation({
    mutationFn: (id) => apiRequests.blog.deletePost(id, { token }),
    onSuccess: () => {
      toast.success("Пост удалён");
      invalidateAll();
    },
  });

  const SitesTable = () => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 10,
    });

    const columns = [
      {
        accessorKey: "name",
        header: "Название",
        cell: ({ row }) => <span>{row.original.name}</span>,
      },
      {
        accessorKey: "token",
        header: "Токен",
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-1">
            <span className="truncate max-w-[200px]">{row.original.token}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => {
                navigator.clipboard.writeText(row.original.token);
                toast.success("Токен скопирован");
              }}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
      {
        accessorKey: "is_active",
        header: "Статус",
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className="rounded-sm border-green-600 bg-white text-black font-sans font-normal flex items-center justify-center gap-1 w-fit mx-auto"
          >
            {row.original.is_active ? (
              <>
                <CheckCircle className="h-3 w-3 text-green-600" />
                Активен
              </>
            ) : (
              "Неактивен"
            )}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Действия",
        cell: ({ row }) => (
          <div className="flex justify-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 border border-neutral-200 hover:bg-neutral-100"
                    onClick={() => {
                      setEditingSite(row.original);
                      setSiteModalOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Редактировать</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 border border-neutral-200 hover:bg-neutral-100"
                    onClick={() => {
                      setSelectedSiteForFolders(row.original);
                      setSiteFoldersModalOpen(true);
                    }}
                  >
                    <FolderOpen className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Управление папками</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border border-neutral-200 hover:bg-neutral-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Удалить сайт?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Сайт будет деактивирован. Это действие можно отменить позже.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => siteDeleteMutation.mutate(row.original.id)}
                  >
                    Удалить
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ),
      },
    ];

    const table = useReactTable({
      data: sites,
      columns,
      state: { sorting, columnFilters, pagination },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    if (sitesLoading) {
      return <Skeleton className="h-40 w-full" />;
    }

    if (sites.length === 0) {
      return (
        <div className="text-center py-8 text-neutral-500 font-sans">
          Нет сайтов
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-neutral-200 overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="text-center font-sans font-normal cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: " 🔼",
                        desc: " 🔽",
                      }[header.column.getIsSorted()] ?? null}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="border-b">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="text-center py-3">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Пагинация */}
        <div className="flex items-center justify-end px-2">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-600">
                Строк на странице:
              </span>
              <Select
                value={table.getState().pagination.pageSize.toString()}
                onValueChange={(value) => table.setPageSize(Number(value))}
                className="w-20"
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="w-20  cursor-pointer"
                >
                  {[10, 20, 30, 40, 50].map((size) => (
                    <SelectItem
                      key={size}
                      value={size.toString()}
                      className="cursor-pointer"
                    >
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1 justify-between items-center">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm text-neutral-600 mx-4">
                Страница {table.getState().pagination.pageIndex + 1} из{" "}
                {table.getPageCount()}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const FoldersTable = () => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 10,
    });

    const columns = [
      {
        accessorKey: "name",
        header: "Название",
        cell: ({ row }) => row.original.name,
      },
      {
        accessorKey: "slug",
        header: "Slug",
        cell: ({ row }) => row.original.slug,
      },
      {
        accessorKey: "path",
        header: "Путь",
        cell: ({ row }) => row.original.path,
      },
      {
        accessorKey: "is_active",
        header: "Активна",
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className="rounded-sm py-1 px-2 border-green-600 bg-white text-black font-sans font-normal flex items-center justify-center gap-1 w-fit mx-auto"
          >
            {row.original.is_active ? (
              <>
                <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                Да
              </>
            ) : (
              "Нет"
            )}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Действия",
        cell: ({ row }) => (
          <div className="flex justify-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border border-neutral-200 hover:bg-neutral-100"
              onClick={() => {
                setEditingFolder(row.original);
                setFolderModalOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border border-neutral-200 hover:bg-neutral-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Удалить папку?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Папка будет деактивирована. Посты останутся.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => folderDeleteMutation.mutate(row.original.id)}
                  >
                    Удалить
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ),
      },
    ];

    const table = useReactTable({
      data: folders,
      columns,
      state: { sorting, columnFilters, pagination },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    if (foldersLoading) {
      return <Skeleton className="h-40 w-full" />;
    }

    if (folders.length === 0) {
      return (
        <div className="text-center py-8 text-neutral-500 font-sans">
          Нет папок
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-neutral-200 overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="text-center font-sans font-normal cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: " 🔼",
                        desc: " 🔽",
                      }[header.column.getIsSorted()] ?? null}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="border-b">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="text-center py-3">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Пагинация */}
        <div className="flex items-center justify-end px-2">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-600">
                Строк на странице:
              </span>
              <Select
                value={table.getState().pagination.pageSize.toString()}
                onValueChange={(value) => table.setPageSize(Number(value))}
                className="w-20"
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="w-20  cursor-pointer"
                >
                  {[10, 20, 30, 40, 50].map((size) => (
                    <SelectItem
                      key={size}
                      value={size.toString()}
                      className="cursor-pointer"
                    >
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1 justify-between items-center">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm text-neutral-600 mx-4">
                Страница {table.getState().pagination.pageIndex + 1} из{" "}
                {table.getPageCount()}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PostsTable = () => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 10,
    });

    const columns = [
      {
        accessorKey: "title",
        header: "Заголовок",
        cell: ({ row }) => (
          <span className="truncate">{row.original.title}</span>
        ),
      },
      {
        accessorKey: "slug",
        header: "Slug",
        cell: ({ row }) => row.original.slug,
      },
      {
        accessorKey: "folder_id",
        header: "Папка",
        cell: ({ row }) => {
          const folder = folders.find((f) => f.id === row.original.folder_id);
          return folder?.name || "—";
        },
      },
      {
        accessorKey: "status",
        header: "Статус",
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className="rounded-sm border-green-600 bg-white text-black font-sans font-normal flex items-center justify-center gap-1 w-fit mx-auto"
          >
            {row.original.status === "PUBLISHED" ? (
              <>
                <CheckCircle className="h-3 w-3 text-green-600" />
                Опубликован
              </>
            ) : (
              "Черновик"
            )}
          </Badge>
        ),
      },
      {
        accessorKey: "tags",
        header: "Теги",
        cell: ({ row }) => (
          <div className="flex flex-wrap justify-center gap-1">
            {row.original.tags?.slice(0, 3).map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="text-xs font-sans font-normal"
              >
                {tag}
              </Badge>
            ))}
            {row.original.tags?.length > 3 && <span>...</span>}
          </div>
        ),
      },
      {
        id: "actions",
        header: "Действия",
        cell: ({ row }) => (
          <div className="flex justify-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border border-neutral-200 hover:bg-neutral-100"
              onClick={() => {
                setEditingPost(row.original);
                setPostModalOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border border-neutral-200 hover:bg-neutral-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Удалить пост?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Пост будет помечен как удалённый.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => postDeleteMutation.mutate(row.original.id)}
                  >
                    Удалить
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ),
      },
    ];

    const table = useReactTable({
      data: posts,
      columns,
      state: { sorting, columnFilters, pagination },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    if (postsLoading) {
      return <Skeleton className="h-40 w-full" />;
    }

    if (posts.length === 0) {
      return (
        <div className="text-center py-8 text-neutral-500 font-sans">
          Нет постов
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-neutral-200 overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="text-center font-sans font-normal cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: " 🔼",
                        desc: " 🔽",
                      }[header.column.getIsSorted()] ?? null}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="border-b">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="text-center py-3">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Пагинация */}
        <div className="flex items-center justify-end px-2">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-600">
                Строк на странице:
              </span>
              <Select
                value={table.getState().pagination.pageSize.toString()}
                onValueChange={(value) => table.setPageSize(Number(value))}
                className="w-20"
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="w-20  cursor-pointer"
                >
                  {[10, 20, 30, 40, 50].map((size) => (
                    <SelectItem
                      key={size}
                      value={size.toString()}
                      className="cursor-pointer"
                    >
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1 justify-between items-center">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm text-neutral-600 mx-4">
                Страница {table.getState().pagination.pageIndex + 1} из{" "}
                {table.getPageCount()}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SiteModal = () => {
    const [name, setName] = useState(editingSite?.name || "");
    const [isActive, setIsActive] = useState(editingSite?.is_active ?? true);

    const handleSubmit = (e) => {
      e.preventDefault();
      const payload = { name, is_active: isActive };
      if (editingSite) {
        siteUpdateMutation.mutate({ id: editingSite.id, data: payload });
      } else {
        siteCreateMutation.mutate({ name });
      }
    };

    return (
      <Dialog open={siteModalOpen} onOpenChange={setSiteModalOpen}>
        <DialogContent className="font-sans">
          <DialogHeader>
            <DialogTitle className="font-sans font-medium">
              {editingSite ? "Редактировать сайт" : "Новый сайт"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label htmlFor="site-name" className="font-sans font-normal">
                  Название
                </Label>
                <Input
                  id="site-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="font-sans"
                />
              </div>
              {editingSite && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="site-active"
                    checked={isActive}
                    onCheckedChange={(checked) => setIsActive(!!checked)}
                  />
                  <Label
                    htmlFor="site-active"
                    className="font-sans font-normal"
                  >
                    Активен
                  </Label>
                </div>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setSiteModalOpen(false)}
                className="font-sans font-normal"
              >
                Отмена
              </Button>
              <Button
                type="submit"
                disabled={
                  siteCreateMutation.isPending || siteUpdateMutation.isPending
                }
                className="font-sans"
              >
                {editingSite ? "Сохранить" : "Создать"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  const SiteFoldersModal = () => {
    const [selectedFolderIds, setSelectedFolderIds] = useState([]);
    useEffect(() => {
      if (siteFoldersModalOpen && selectedSiteForFolders) {
        apiRequests.blog
          .getSiteFolders(selectedSiteForFolders.id, { token })
          .then((res) => setSelectedFolderIds(res.data.folder_ids || []))
          .catch(() => toast.error("Не удалось загрузить папки сайта"))
          .finally();
      } // eslint-disable-next-line
    }, [siteFoldersModalOpen, selectedSiteForFolders, token]);

    // Построение дерева папок из плоского списка
    const buildFolderTree = (flatFolders) => {
      const map = {};
      const roots = [];
      flatFolders.forEach((folder) => {
        map[folder.id] = { ...folder, children: [] };
      });
      flatFolders.forEach((folder) => {
        if (folder.parent_id && map[folder.parent_id]) {
          map[folder.parent_id].children.push(map[folder.id]);
        } else {
          roots.push(map[folder.id]);
        }
      });
      return roots;
    };

    const folderTree = buildFolderTree(folders);

    // Рекурсивный рендер узла дерева
    const renderFolderNode = (node, level = 0) => (
      <div key={node.id}>
        <label
          className="flex items-center gap-2 cursor-pointer py-1"
          style={{ paddingLeft: `${level * 20}px` }}
        >
          <Checkbox
            checked={selectedFolderIds.includes(node.id)}
            onCheckedChange={() => toggleFolder(node.id)}
          />
          <span className="text-sm font-sans">
            {node.name}{" "}
            <span className="text-neutral-400 text-xs">({node.path})</span>
          </span>
        </label>
        {node.children?.length > 0 &&
          node.children.map((child) => renderFolderNode(child, level + 1))}
      </div>
    );

    const toggleFolder = (folderId) => {
      setSelectedFolderIds((prev) =>
        prev.includes(folderId)
          ? prev.filter((id) => id !== folderId)
          : [...prev, folderId],
      );
    };

    const handleSave = () => {
      siteFoldersMutation.mutate({
        siteId: selectedSiteForFolders.id,
        folderIds: selectedFolderIds,
      });
    };

    return (
      <Dialog
        open={siteFoldersModalOpen}
        onOpenChange={setSiteFoldersModalOpen}
      >
        <DialogContent className="max-w-md font-sans">
          <DialogHeader>
            <DialogTitle className="font-sans font-medium">
              Папки сайта: {selectedSiteForFolders?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 max-h-[400px] overflow-y-auto">
            {folderTree.length === 0 ? (
              <div className="text-center py-4 text-neutral-500">
                Нет доступных папок
              </div>
            ) : (
              folderTree.map((node) => renderFolderNode(node))
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSiteFoldersModalOpen(false)}
              className="font-sans font-normal"
            >
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={siteFoldersMutation.isPending}
              className="font-sans"
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const FolderModal = () => {
    const [form, setForm] = useState({
      name: editingFolder?.name || "",
      slug: editingFolder?.slug || "",
      parent_id: editingFolder?.parent_id || "",
      is_active: editingFolder?.is_active ?? true,
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      const payload = {
        name: form.name,
        slug: form.slug,
        parent_id: normalizeParentId(form.parent_id),
        is_active: form.is_active,
      };
      if (editingFolder) {
        folderUpdateMutation.mutate({ id: editingFolder.id, data: payload });
      } else {
        folderCreateMutation.mutate(payload);
      }
    };

    return (
      <Dialog open={folderModalOpen} onOpenChange={setFolderModalOpen}>
        <DialogContent className="font-sans">
          <DialogHeader>
            <DialogTitle className="font-sans font-medium">
              {editingFolder ? "Редактировать папку" : "Новая папка"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label className="font-sans font-normal">Название</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="font-sans"
                />
              </div>
              <div className="space-y-1">
                <Label className="font-sans font-normal">
                  Slug (псевдоним)
                </Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  required
                  className="font-sans"
                />
              </div>
              <div className="space-y-1 flex justify-between">
                <div className="flex flex-col gap-1">
                  <Label className="font-sans font-normal">
                    Родительская папка
                  </Label>
                  <Select
                    value={form.parent_id?.toString() || "__none__"}
                    onValueChange={(v) =>
                      setForm({ ...form, parent_id: v === "__none__" ? "" : v })
                    }
                    className="w-full"
                  >
                    <SelectTrigger className="font-sans cursor-pointer">
                      <SelectValue placeholder="Нет (корневая)" />
                    </SelectTrigger>
                    <SelectContent
                      position="popper"
                      className="z-[1100] w-full"
                    >
                      <SelectItem
                        value="__none__"
                        className="font-sans cursor-pointer w-full"
                      >
                        Нет (корневая)
                      </SelectItem>
                      {folders.map((f) => (
                        <SelectItem
                          key={f.id}
                          value={f.id.toString()}
                          className="font-sans cursor-pointer"
                        >
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="folder-active"
                    checked={form.is_active}
                    onCheckedChange={(checked) =>
                      setForm({ ...form, is_active: !!checked })
                    }
                  />
                  <Label
                    htmlFor="folder-active"
                    className="font-sans font-normal"
                  >
                    Активна
                  </Label>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => setFolderModalOpen(false)}
                className="font-sans font-normal"
              >
                Отмена
              </Button>
              <Button
                type="submit"
                disabled={
                  folderCreateMutation.isPending ||
                  folderUpdateMutation.isPending
                }
                className="font-sans"
              >
                {editingFolder ? "Сохранить" : "Создать"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  const PostModal = () => {
  const [form, setForm] = useState({
    folder_id: editingPost?.folder_id || "",
    title: editingPost?.title || "",
    slug: editingPost?.slug || "",
    content: editingPost?.content || "",
    excerpt: editingPost?.excerpt || "",
    status: editingPost?.status || "DRAFT",
    tags: editingPost?.tags || [],
  });

  const [pictures, setPictures] = useState([]);
  const [picturesLoading, setPicturesLoading] = useState(false);
  const [tempFiles, setTempFiles] = useState([]);

  // Загрузка существующих изображений при редактировании
  useEffect(() => {
    if (editingPost?.id && postModalOpen) {
      setPicturesLoading(true);
      apiRequests.pictures
        .getItemsOrder("blog_posts", editingPost.id, token)
        .then((res) => setPictures(res.data.result || []))
        .catch(() => toast.error("Не удалось загрузить изображения"))
        .finally(() => setPicturesLoading(false));
    } else {
      setPictures([]);
    } // eslint-disable-next-line
  }, [editingPost?.id, postModalOpen, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      folder_id: parseInt(form.folder_id),
      title: form.title,
      slug: form.slug,
      content: form.content,
      excerpt: form.excerpt || null,
      status: form.status,
      tag_slugs: form.tags,
    };

    let postId = editingPost?.id;
    try {
      if (editingPost) {
        await postUpdateMutation.mutateAsync({
          id: editingPost.id,
          data: payload,
        });
      } else {
        const result = await postCreateMutation.mutateAsync(payload);
        // Предполагаем, что ответ содержит поле id
        postId = result?.id || result?.data?.id;
      }

      // Загружаем временные файлы
      if (tempFiles.length > 0 && postId) {
        const uploadPromises = tempFiles.map((file) => {
          const formData = new FormData();
          formData.append("file", file);
          return apiRequests.pictures.postPictureOrder(
            "blog_posts",
            postId,
            token,
            formData,
          );
        });
        await Promise.all(uploadPromises);
        toast.success("Изображения загружены");
      }

      invalidateAll();
      setPostModalOpen(false);
    } catch (error) {
      toast.error("Ошибка при сохранении");
    }
  };

  const handleDeletePicture = async (pictureId) => {
    try {
      await apiRequests.pictures.removeImage(token)(pictureId);
      setPictures((prev) => prev.filter((p) => p.id !== pictureId));
      toast.success("Изображение удалено");
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  return (
    <Dialog open={postModalOpen} onOpenChange={setPostModalOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto font-sans">
        <DialogHeader>
          <DialogTitle className="font-sans font-medium">
            {editingPost ? "Редактировать пост" : "Новый пост"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Поля контента */}
          <div className="space-y-1">
            <Label className="font-sans font-normal">Заголовок</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1">
            <Label className="font-sans font-normal">Slug (псевдоним)</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1">
            <Label className="font-sans font-normal">Краткое описание</Label>
            <Textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
            />
          </div>
          <div className="space-y-1">
            <Label className="font-sans font-normal">Содержание</Label>
            <HtmlEditor
              value={form.content}
              onChange={(html) => setForm({ ...form, content: html })}
            />
          </div>

          {/* Статус и Папка в строку */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label className="font-sans font-normal mb-1">Статус</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[1100]">
                  <SelectItem value="DRAFT">Черновик</SelectItem>
                  <SelectItem value="PUBLISHED">Опубликован</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="font-sans font-normal mb-1">Папка</Label>
              <Select
                value={form.folder_id?.toString()}
                onValueChange={(v) => setForm({ ...form, folder_id: v })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Выберите папку" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[1100]">
                  {folders.length === 0 ? (
                    <div className="p-2 text-sm text-neutral-500">
                      Нет доступных папок
                    </div>
                  ) : (
                    folders.map((f) => (
                      <SelectItem key={f.id} value={f.id.toString()}>
                        {f.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Теги */}
          <div className="space-y-1">
            <Label className="font-sans font-normal">Теги</Label>
            <TagInput
              value={form.tags}
              onChange={(newTags) => setForm({ ...form, tags: newTags })}
            />
          </div>

          {/* Блок медиа */}
          <div className="border rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3 font-sans font-normal">
              Изображения
            </h3>
            <ImageUploader
              onFilesSelected={setTempFiles}
            />
            {tempFiles.length > 0 && (
              <div className="mt-2">
                <p className="text-sm mb-1">Новые изображения:</p>
                <div className="grid grid-cols-3 gap-2">
                  {tempFiles.map((file, idx) => (
                    <div key={`temp-${idx}`} className="relative group border rounded">
                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="w-full h-24 object-cover rounded"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6 bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition"
                        onClick={() => setTempFiles(prev => prev.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Галерея существующих изображений */}
          {pictures.length > 0 && (
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-normal mb-3 font-sans">Галерея</h3>
              {picturesLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (
                <ScrollArea className="h-[200px]">
                  <div className="grid grid-cols-3 gap-2">
                    {pictures.map((pic) => (
                      <div key={pic.id} className="relative group border rounded">
                        <img
                          src={pic.public_url || pic.url}
                          alt=""
                          className="w-full h-24 object-cover rounded"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-1 right-1 h-6 w-6 bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition"
                          onClick={() => handleDeletePicture(pic.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPostModalOpen(false)}
              className="font-sans font-normal"
            >
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={postCreateMutation.isPending || postUpdateMutation.isPending}
              className="font-sans"
            >
              {editingPost ? "Сохранить" : "Создать"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

  const renderActionButton = () => {
    switch (activeTab) {
      case "sites":
        return (
          <Button
            onClick={() => {
              setEditingSite(null);
              setSiteModalOpen(true);
            }}
            className="font-sans"
          >
            <Plus className="h-4 w-4 mr-1" /> Создать сайт
          </Button>
        );
      case "folders":
        return (
          <Button
            onClick={() => {
              setEditingFolder(null);
              setFolderModalOpen(true);
            }}
            className="font-sans"
          >
            <FolderPlus className="h-4 w-4 mr-1" /> Создать папку
          </Button>
        );
      case "posts":
        return (
          <Button
            onClick={() => {
              setEditingPost(null);
              setPostModalOpen(true);
            }}
            className="font-sans"
          >
            <FileText className="h-4 w-4 mr-1" /> Создать пост
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4 space-y-4 font-sans">
      <div className="flex items-center justify-between">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between mb-4">
            <TabsList className="bg-muted p-1 rounded-lg w-150 gap-2">
              <TabsTrigger
                value="sites"
                className="w-30 h-10 text-md font-normal gap-1.5 data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:border-b-2 data-[state=active]:border-black border-l-0 border-t-0 border-r-0 px-4 py-2 transition-all duration-200 font-inter"
              >
                Сайты
              </TabsTrigger>
              <TabsTrigger
                value="folders"
                className="w-30 h-10 text-md font-normal gap-1.5  data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:border-b-2 data-[state=active]:border-black border-l-0 border-t-0 border-r-0 px-4 py-2 transition-all duration-200 font-inter"
              >
                Папки
              </TabsTrigger>
              <TabsTrigger
                value="posts"
                className="w-30 h-10 text-md font-normal gap-1.5  data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:border-b-2 data-[state=active]:border-black border-l-0 border-t-0 border-r-0 px-4 py-2 transition-all duration-200 font-inter"
              >
                Посты
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              {activeTab === "posts" && (
                <>
                  <Select
                    value={postFolderFilter}
                    onValueChange={setPostFolderFilter}
                  >
                    <SelectTrigger className="w-[180px] font-sans">
                      <SelectValue placeholder="Все папки" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[1100]">
                      <SelectItem
                        value="all"
                        className="font-sans cursor-pointer"
                      >
                        Все папки
                      </SelectItem>
                      {folders.map((f) => (
                        <SelectItem
                          key={f.id}
                          value={f.id.toString()}
                          className="font-sans cursor-pointer"
                        >
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={postStatusFilter}
                    onValueChange={setPostStatusFilter}
                  >
                    <SelectTrigger className="w-[150px] font-sans cursor-pointer">
                      <SelectValue placeholder="Все статусы" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[1100]">
                      <SelectItem
                        value="all"
                        className="font-sans cursor-pointer"
                      >
                        Все статусы
                      </SelectItem>
                      <SelectItem
                        value="DRAFT"
                        className="font-sans cursor-pointer"
                      >
                        Черновик
                      </SelectItem>
                      <SelectItem
                        value="PUBLISHED"
                        className="font-sans cursor-pointer"
                      >
                        Опубликован
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              {renderActionButton()}
            </div>
          </div>

          <TabsContent value="sites" className="mt-0">
            <div className="rounded-lg">
              <SitesTable />
            </div>
          </TabsContent>

          <TabsContent value="folders" className="mt-0">
            <div className="rounded-lg">
              <FoldersTable />
            </div>
          </TabsContent>

          <TabsContent value="posts" className="mt-0">
            <div className="rounded-lg h-full">
              <PostsTable />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <SiteModal />
      <SiteFoldersModal />
      <FolderModal />
      <PostModal />
    </div>
  );
};

export default BlogPage;
